import formatDateTime from 'utils/formatDateTime';
import dataProviderToTicketProvider from 'utils/dataProviderToTicketProvider';

const timeOfDayMapping = {};

let now = new Date();
let tomorrow = new Date(+now);
tomorrow.setDate(tomorrow.getDate() + 1);
let beginningOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
let todayStr = formatDateTime(now, 'dd\\/MM\\/yyyy');
let tomorrowStr = formatDateTime(tomorrow, 'dd\\/MM\\/yyyy');
let datesCache = {};

export default class Screening {
	static clearDatesCache() {
		datesCache = {};
	}

	constructor(id, attr, movie, appData) {
		let copy = appData.copy;

		// Make all attributes from attr available (e.g. time, url, theater_id, cta, ...)
		Object.assign(this, attr);
		if (attr.tz_offset) {
			this.tz_offset = getTZOffsetString(attr.tz_offset);
		}

		let ticketProvider = dataProviderToTicketProvider[attr.provider_id];
		if (ticketProvider && this.url) {
			if (!this.srcTicketProviders) {
				this.srcTicketProviders = {};
			}
			this.srcTicketProviders[ticketProvider] = this.url;
			delete this.url;
		}

		this.id = id;
		this.movie = movie;

		this.providerScreeningId = attr.provider_screening_id;

		this.setDateTime(attr.date, attr.time, copy);
	}

	getSTDate(iso, copy, force) {
		// Force is breaking refs, you should use Screening.clearDatesCache() instead
		force = false;
		if (force || !datesCache[iso]) {
			datesCache[iso] = new STDate(iso, copy);
		}
		return datesCache[iso];
	}

	createDatetime(date, hour, minute) {
		let datetime = new Date(date);
		datetime.setHours(hour, minute, 0, 0);
		return datetime;
	}

	setDateTime(date, time, copy, force) {
		this.date = date;
		this.stDate = date && this.getSTDate(date, copy, force);
		if (time) {
			this.time24 = time;
			let timeSplit = time.split(':');
			this.hour = +timeSplit[0];
			this.minute = +timeSplit[1];

			let timeOfDay = new TimeOfDay(this.hour, copy);
			if (!force && timeOfDayMapping[timeOfDay.id])
				this.timeOfDay = timeOfDayMapping[timeOfDay.id];
			else {
				this.timeOfDay = (timeOfDayMapping[timeOfDay.id] = timeOfDay);
			}

			this.datetime = this.createDatetime(this.stDate ? this.stDate.date : now, this.hour, this.minute);

			if (this.hour <= 2) {
				let nextDay = new Date(date || +now);
				nextDay.setDate(nextDay.getDate() + 1);
				this.nextDaySTDate = this.getSTDate(nextDay.toISOString(), copy);
				this.nextDayDatetime = this.createDatetime(this.nextDaySTDate ? this.nextDaySTDate.date : now, this.hour, this.minute);
			} else {
				delete this.nextDaySTDate;
				delete this.nextDayDatetime;
			}

			this.isPast = (this.nextDayDatetime || this.datetime) < now;
		} else {
			delete this.time24;
			delete this.hour;
			delete this.minute;
			delete this.timeOfDay;
			delete this.datetime;
			delete this.nextDaySTDate;
			delete this.nextDayDatetime;
			if (this.stDate) {
				this.isPast = this.stDate.date < beginningOfToday;
			}
		}
	}

	offsetDate(days, months, years, copy) {
		if (!this.date) return;
		let parts = this.date.slice(0, 10).split('-');
		let yoff = (+years || 0);
		let moff = (+months || 0);
		let doff = (+days || 0);
		let d = new Date(Date.UTC(+parts[0] + yoff, parts[1] - 1 + moff, +parts[2] + doff));
		this.setDateTime(d.toISOString(), this.time24, copy);
	}
}

export class STDate {
	constructor(dateStr, copy) {
		//2016-08-04
		let year = +dateStr.slice(0, 4);
		let month = +dateStr.slice(5, 7);
		let day = +dateStr.slice(8, 10);
		let date = new Date(year, month - 1, day);

		//  TESTING: REMOVE THIS
		// date.setDate(date.getDate() + ~~(Math.random() * 10));
		// !!!!!!!!!!!!!!!!!
		this.id = date.valueOf();
		this.date = date;

		// this.dayShort = copy.$DATETIME_LOCALE.$DAYS_SHORT[date.getDay()];
		// this.monthShort = copy.$DATETIME_LOCALE.$MONTHS_SHORT[date.getMonth()];
		// this.dateNum = ('00'+date.getDate()).slice(-2);
		// We could format only where needed
		this.dayShort = formatDateTime(date, 'ddd', copy.$DATETIME_LOCALE);
		this.monthShort = formatDateTime(date, 'MMM', copy.$DATETIME_LOCALE);
		this.dateNum = formatDateTime(date, 'dd', copy.$DATETIME_LOCALE);
		// \\/ because we always want an actual '/' instead of the locale date separator (which is a / anyway because we don't pass a locale)
		this.ddmmyyyy = formatDateTime(date, 'dd\\/MM\\/yyyy');
		this.yyyymmdd = formatDateTime(date, 'yyyy-MM-dd');

		this.label = formatDateTime(date, copy.$DATE_FILTER_FORMAT || 'ddd MMMMM dd', copy.$DATETIME_LOCALE);
		// this.label = this.dayShort + ' ' + this.monthShort.toUpperCase() + ' ' + this.dateNum;

		if (this.ddmmyyyy === todayStr) {
			this.label = copy.$TODAY;
		} else if (this.ddmmyyyy === tomorrowStr) {
			this.label = copy.$TOMORROW;
		}
	}
}

class TimeOfDay {
	constructor(hour24, copy) {
		this.id = getTimeOfDayId(hour24);
		this.label = copy[this.id];
	}
}

function getTimeOfDayId(hour24) {
	if (hour24 >= 22 || hour24 <= 3) {
		return '$LATE';
	} else if (hour24 >= 18 && hour24 < 22) {
		return '$EVENING';
	} else if (hour24 >= 12 && hour24 < 18) {
		return '$AFTERNOON';
	} else if (hour24 > 3 && hour24 < 12) {
		return '$MORNING';
	} else {
		return '$UNKNOWN';
	}
}

function getTZOffsetString(offset) {
	let sign = (offset.hours && offset.hours < 0) || (offset.minutes && offset.minutes < 0) ? '-' : '+';
	let hours = offset.hours ? Math.abs(offset.hours) : 0;
	hours = hours < 10 ? `0${hours}` : hours.toString();
	let minutes = offset.minutes ? Math.abs(offset.hours) : 0;
	minutes = minutes < 10 ? `0${minutes}` : minutes.toString();

	return `${sign}${hours}:${minutes}`;
}
